import { lengthContraction, lengthEnlargement } from '@/domain/physics/SpecialRelativity.js'
import SceneGeometry, { SCENE_HEIGHT_REL } from '@/domain/geometry/SceneGeometry.js'
import Platform, { PLATFORM_IMG } from '@/domain/scene/Platform.js'
import TrainTracks, { TRAIN_TRACKS_IMG } from '@/domain/scene/TrainTracks.js'
import Train, { TRAIN_IMG } from '@/domain/scene/Train.js'
import { PLATFORM_LIGHT_IMG } from '@/domain/scene/lights/generic/PlatformLight.js'
import { TRAIN_LIGHT_IMG } from '@/domain/scene/lights/generic/TrainLight.js'
import { LIGHT_T5_IMG } from '@/domain/scene/lights/train/LightT5.js'
import Observer, { OBSERVER_IMG, OBSERVER_INIT_Y_REL, OBSERVER_INIT_Y_IN_TRAIN_REL } from '@/domain/scene/Observer.js'
import PlatformGroup from '@/domain/scene/PlatformGroup.js'
import TrainGroup from '@/domain/scene/TrainGroup.js'
import ObserverGroup from '@/domain/scene/ObserverGroup.js'
import sceneStore from '@/vuex/sceneStore'
import Indicators from '@/domain/scene/indicators/Indicators.js'
import Lights from '@/domain/scene/lights/Lights.js'

export default class Scene {
  constructor (sceneId, title, physics, speedValue, speedUnit) {
    this.store = sceneStore

    this.sceneId = sceneId
    this.title = title
    this.speedValue = speedValue
    this.physics = physics
    this.speedValue = speedValue
    this.speedUnit = speedUnit

    if (this.physics === 'classic' && this.speedUnit !== 'm/s') {
      throw new Error('For "classic" physics speed unit must be "m/s".')
    }
    if (this.physics === 'relativistic' && this.speedUnit !== 'c') {
      throw new Error('For "relativistic" physics speed unit must be "c".')
    }
    this.isRelativistic = (this.physics === 'relativistic')
    this.speedC = this.isRelativistic ? this.speedValue : null

    this.isSimulationPlaying = false

    this.platform = null
    this.trainTracks = null
    this.train = null
    this.observer = null
    this.platformGroup = null
    this.trainGroup = null
    this.observerGroup = null

    this.platformGroupCurrentX = null
    this.trainGroupCurrentX = null
    this.observerGroupCurrentX = null

    this.indicators = new Indicators()
    this.lights = new Lights()
    this.lightsEnabled = false

    this.sceneImages = {
      'platform': { 'image': null, 'src': PLATFORM_IMG },
      'train': { 'image': null, 'src': TRAIN_IMG },
      'observer': { 'image': null, 'src': OBSERVER_IMG },
      'trainTracks': { 'image': null, 'src': TRAIN_TRACKS_IMG },
      'platformLight': { 'image': null, 'src': PLATFORM_LIGHT_IMG },
      'trainLight': { 'image': null, 'src': TRAIN_LIGHT_IMG },
      'trainLightLocomotive': { 'image': null, 'src': LIGHT_T5_IMG }
    }
  }

  updateStore (isObserverInTrain, observerPosM, oppositeObjPosM, sceneShiftM, lightsEnabled) {
    this.store.commit('changeIsObserverInTrain', { sceneId: this.sceneId, value: isObserverInTrain })
    this.store.commit('changeObserverPos', { sceneId: this.sceneId, value: observerPosM })
    this.store.commit('changeOppositeObjPos', { sceneId: this.sceneId, value: oppositeObjPosM })
    this.store.commit('changeSceneShift', { sceneId: this.sceneId, value: sceneShiftM })
    this.store.commit('changeLightsEnabled', { sceneId: this.sceneId, value: lightsEnabled })
  }

  updateScene () {
    let isObserverInTrain = this.observerGroup.isInTrain
    let observerPosM = this.sceneGeometry.px2m(this.observerGroupCurrentX)
    let oppositeObjPosM
    if (isObserverInTrain) {
      oppositeObjPosM = this.sceneGeometry.px2m(this.platformGroupCurrentX - this.trainGroupCurrentX)
    } else {
      oppositeObjPosM = this.sceneGeometry.px2m(this.trainGroupCurrentX - this.platformGroupCurrentX)
    }
    let sceneShiftM = this.sceneGeometry.px2m(this.platformGroupCurrentX - this.halfWidth)

    this.updateStore(isObserverInTrain, observerPosM, oppositeObjPosM, sceneShiftM, this.lightsEnabled)

    this.indicators.setOnScene(this.width, this.height, this.observerGroup.isInTrain, this.speedC)
    this.indicators.setValues(isObserverInTrain, observerPosM, oppositeObjPosM, this.isRelativistic, this.speedValue)
    this.lights.setOnScene(this.width, this.height, this.observerGroup.isInTrain, this.speedC, this.sceneImages)
    this.lights.setValues(this.lightsEnabled, isObserverInTrain, observerPosM, oppositeObjPosM, this.isRelativistic, this.speedValue)

    this.setObserverGroupDraggable()
  }

  setObserverGroupDraggable () {
    let parent = this.observerGroup.isInTrain ? this.train : this.platform
    this.observerGroup.setDraggable(parent)
  }

  setSceneSizes (width) {
    this.width = width
    this.height = width * SCENE_HEIGHT_REL
    this.halfWidth = this.width / 2
    this.halfHeight = this.height / 2
    this.sceneGeometry = new SceneGeometry(width)
  }

  setElements (width) {
    this.setSceneSizes(width)

    this.platform = new Platform()
    this.platform.setOnScene(this.width, this.height)
    this.platform.setImage(this.sceneImages['platform']['image'])

    this.trainTracks = new TrainTracks()
    this.trainTracks.setOnScene(this.width, this.height)
    this.trainTracks.setImage(this.sceneImages['trainTracks']['image'])

    this.train = new Train()
    this.train.setOnScene(this.width, this.height, this.speedC)
    this.train.setImage(this.sceneImages['train']['image'])

    this.observer = new Observer()
    this.observer.setOnScene(this.width, this.height)
    this.observer.setImage(this.sceneImages['observer']['image'])

    this.observerGroup = new ObserverGroup()
    this.observerGroup.isInTrain = false
    this.observerGroup.setOnScene(this.width, this.height)
    this.setObserverGroupDraggable()

    this.platformGroup = new PlatformGroup()
    this.platformGroup.setOnScene(this.width, this.height)

    this.trainGroup = new TrainGroup()
    this.trainGroup.setOnScene(this.width, this.height)
    this.trainGroup.setDraggable(this.width)

    this.setElementsFromStore()

    this.platformGroupCurrentX = this.platformGroup.x
    this.trainGroupCurrentX = this.trainGroup.x
    this.observerGroupCurrentX = this.observerGroup.x
    this.updateScene()
  }

  getObserverY (isInTrain, sceneHeight) {
    let yRel = (isInTrain) ? OBSERVER_INIT_Y_IN_TRAIN_REL : OBSERVER_INIT_Y_REL
    return yRel * sceneHeight - this.observer.halfHeight
  }

  setElementsFromStore () {
    if (!(this.sceneId in this.store.state.observerPos)) {
      return
    }

    let storageObserverPos = this.store.state.observerPos[this.sceneId]
    let storageOppositeObjPos = this.store.state.oppositeObjPos[this.sceneId]
    let storageSceneShift = this.store.state.sceneShift[this.sceneId]
    let lightsEnabled = this.store.state.lightsEnabled[this.sceneId]

    this.lightsEnabled = lightsEnabled

    this.platformGroup.x = this.sceneGeometry.m2px(storageSceneShift) + this.halfWidth

    if (this.store.state.isObserverInTrain[this.sceneId] === true) {
      this.observerGroup.isInTrain = true
      this.trainGroup.x = this.sceneGeometry.m2px(-storageOppositeObjPos) + this.platformGroup.x
      this.observerGroup.x = this.sceneGeometry.m2px(storageObserverPos)

      this.platform.setOnScene(this.width, this.height, this.speedC)
      this.trainTracks.setOnScene(this.width, this.height, this.speedC)
      this.train.setOnScene(this.width, this.height)
      this.observer.setOnScene(this.width, this.height)
      this.observer.y = this.getObserverY(true, this.height)
    } else {
      this.observerGroup.isInTrain = false
      this.trainGroup.x = this.sceneGeometry.m2px(storageOppositeObjPos) + this.platformGroup.x
      this.observerGroup.x = this.sceneGeometry.m2px(storageObserverPos)

      this.platform.setOnScene(this.width, this.height)
      this.trainTracks.setOnScene(this.width, this.height)
      this.train.setOnScene(this.width, this.height, this.speedC)
      this.observer.setOnScene(this.width, this.height)
    }
  }

  getCanvasConfig () {
    return {
      width: this.width,
      height: this.height
    }
  }

  getObserverAbsoluteX () {
    let observerAbsoluteX
    if (this.observerGroup.isInTrain) {
      observerAbsoluteX = this.trainGroupCurrentX + this.observerGroupCurrentX
    } else {
      observerAbsoluteX = this.platformGroupCurrentX + this.observerGroupCurrentX
    }
    return observerAbsoluteX
  }

  getPlatformXAfterJump (currentX, inTrain, observerAbsX, speedC) {
    let oldDist = currentX - observerAbsX
    let newDist
    if (inTrain) {
      newDist = lengthEnlargement(oldDist, speedC)
    } else {
      newDist = lengthContraction(oldDist, speedC)
    }
    let newX = observerAbsX + newDist
    return newX
  }

  getTrainXAfterJump (currentX, inTrain, observerAbsX, speedC) {
    let oldDist = currentX - observerAbsX
    let newDist
    if (inTrain) {
      newDist = lengthContraction(oldDist, speedC)
    } else {
      newDist = lengthEnlargement(oldDist, speedC)
    }
    let newX = observerAbsX + newDist
    return newX
  }

  getObserverXAfterJump (observerAbsX, newContainerX) {
    let newX = observerAbsX - newContainerX
    return newX
  }

  getObserverYAfterJump (newIsInTrain, sceneHeight) {
    return this.getObserverY(newIsInTrain, sceneHeight)
  }

  isObserverPassingTrain () {
    let observerAbsoluteX = this.getObserverAbsoluteX(this.observerGroup.isInTrain)
    let minPos
    let maxPos
    if (this.observerGroup.isInTrain) {
      minPos = this.platformGroupCurrentX - this.platform.halfWidth
      maxPos = this.platformGroupCurrentX + this.platform.halfWidth
    } else {
      minPos = this.trainGroupCurrentX - this.train.halfWidth
      maxPos = this.trainGroupCurrentX + this.train.halfWidth
    }
    if (minPos <= observerAbsoluteX && observerAbsoluteX <= maxPos) {
      return true
    } else {
      return false
    }
  }

  canObserverJump () {
    return this.isObserverPassingTrain()
  }

  jumpObserver () {
    if (this.isSimulationPlaying) {
      this.isSimulationPlaying = false
      return
    }

    if (!this.canObserverJump()) {
      return
    }

    // --- Auxiliary values -----------------------
    let observerAbsX = this.getObserverAbsoluteX(this.observerGroup.isInTrain)
    let inTrain = this.observerGroup.isInTrain

    // --- Compute new values -----------------------
    let newIsInTrain = !inTrain
    let newPlatformGroupX
    let newTrainGroupX
    if (this.isRelativistic) {
      newPlatformGroupX = this.getPlatformXAfterJump(this.platformGroupCurrentX, inTrain, observerAbsX, this.speedC)
      newTrainGroupX = this.getTrainXAfterJump(this.trainGroupCurrentX, inTrain, observerAbsX, this.speedC)
    } else {
      newPlatformGroupX = this.platformGroupCurrentX
      newTrainGroupX = this.trainGroupCurrentX
    }
    let newContainerX = newIsInTrain ? newTrainGroupX : newPlatformGroupX
    let newObserverGroupX = this.getObserverXAfterJump(observerAbsX, newContainerX)
    let newObserverY = this.getObserverYAfterJump(newIsInTrain, this.height)

    // --- Set new values -----------------------
    if (this.isRelativistic) {
      if (newIsInTrain) {
        this.platform.setOnScene(this.width, this.height, this.speedC)
        this.trainTracks.setOnScene(this.width, this.height, this.speedC)
        this.train.setOnScene(this.width, this.height)
      } else {
        this.platform.setOnScene(this.width, this.height)
        this.trainTracks.setOnScene(this.width, this.height)
        this.train.setOnScene(this.width, this.height, this.speedC)
      }
    }
    this.observerGroup.isInTrain = newIsInTrain
    this.platformGroupCurrentX = this.platformGroup.x = newPlatformGroupX
    this.trainGroupCurrentX = this.trainGroup.x = newTrainGroupX
    this.observerGroupCurrentX = this.observerGroup.x = newObserverGroupX
    this.observer.y = newObserverY

    this.updateScene()
  }

  playPause () {
    this.isSimulationPlaying = !this.isSimulationPlaying

    if (!this.isSimulationPlaying) {
      return
    }

    const FRAME_DURATION = 1000 / 60
    const getTime = typeof performance === 'function' ? performance.now : Date.now
    let position = this.trainGroupCurrentX
    let lastUpdate = getTime()

    let that = this

    function animate () {
      const now = getTime()
      const delta = (now - lastUpdate) / FRAME_DURATION
      position += that.width * delta / 1000

      if (position >= that.width) {
        that.isSimulationPlaying = false
        position = that.width
      }

      that.trainGroup.x = position
      that.trainGroupCurrentX = position
      that.updateScene()

      lastUpdate = now

      if (that.isSimulationPlaying) {
        setTimeout(animate, 10)
      }
    }

    animate()
  }

  resetSimulation () {
    if (this.isSimulationPlaying) {
      let that = this
      setTimeout(function () {
        that.resetSimulation()
      }, 30)
      this.isSimulationPlaying = false
      return
    }

    this.platform.setOnScene(this.width, this.height)
    this.trainTracks.setOnScene(this.width, this.height)
    this.train.setOnScene(this.width, this.height, this.speedC)

    this.observerGroup.isInTrain = false
    this.platformGroupCurrentX = this.platformGroup.x = this.halfWidth
    this.trainGroupCurrentX = this.trainGroup.x = 0
    this.observerGroupCurrentX = this.observerGroup.x = 0
    this.observer.y = this.getObserverY(false, this.height)

    this.updateScene()
  }

  switchLights (width) {
    this.lightsEnabled = !this.lightsEnabled
    this.store.commit('changeLightsEnabled', { sceneId: this.sceneId, value: this.lightsEnabled })
    this.setElements(width)
  }

  preloadImages (width) {
    this.loadingImagesCount = Object.keys(this.sceneImages).length

    for (var key in this.sceneImages) {
      let img = new Image()
      let that = this
      img.onload = function () {
        that.loadingImagesCount--
        if (that.loadingImagesCount === 0) {
          that.setElements(width)
        }
      }
      img.src = this.sceneImages[key]['src']
      this.sceneImages[key]['image'] = img
    }
  }

  lengthContraction (length0) {
    if (this.speedC === null) {
      return length0
    }
    return lengthContraction(length0, this.speedC)
  }
}
